import { useCallback, useState } from 'react';
import { container } from '@infrastructure/container';
import { useToast } from '@shared/hooks/useToast';
import { purchaseOrderPdfFileName, triggerBlobDownload } from '../utils/purchaseOrderPdf';

export interface UsePurchaseOrderPdfActionsArgs {
  purchaseOrderId: string | undefined;
  poNumber?: string;
}

export function usePurchaseOrderPdfActions({
  purchaseOrderId,
  poNumber,
}: UsePurchaseOrderPdfActionsArgs) {
  const toast = useToast();
  const [isPdfBusy, setIsPdfBusy] = useState(false);

  const fetchPdf = useCallback(async (): Promise<Blob | null> => {
    if (!purchaseOrderId) return null;
    const result = await container.purchasingRepository.getPurchaseOrderPdf(purchaseOrderId);
    if (result.status === 'error') {
      toast.error(result.error.message ?? 'Could not load the purchase order PDF');
      return null;
    }
    return result.data;
  }, [purchaseOrderId, toast]);

  const downloadOrderPdf = useCallback(async () => {
    if (!purchaseOrderId) return;
    setIsPdfBusy(true);
    try {
      const blob = await fetchPdf();
      if (!blob) return;
      triggerBlobDownload(blob, purchaseOrderPdfFileName(poNumber, purchaseOrderId));
    } finally {
      setIsPdfBusy(false);
    }
  }, [purchaseOrderId, poNumber, fetchPdf]);

  return { isPdfBusy, downloadOrderPdf };
}
